import { assign, DoneInvokeEvent } from "xstate";
import {
  getObjectKeysAsObject,
  ParentState,
  setContextWithKey,
} from "@capone/checkout";
import { IPerson, PassengerError } from "redmond";
import { uniq } from "lodash-es";

import { ActivityContext, ActivityMachineContext } from "../../../modules/book/state/types";
import {
  AnswerBookingQuestionsEvent,
  SelectTravelerEvent,
  setCurrentTravelerEvent,
} from "../../../modules/book/state/events";
import { travelerHasMissingRequiredFields } from "./validation";
import { isCheckoutImprovementsEnabled } from "../../../context/experiments";

export const actions = {
  selectTraveler: assign((ctx: ActivityContext, event: SelectTravelerEvent) => {
    const { singleTravelerWorkflow } = event;
    const onUpdate = event?.data?.onUpdate || false;

    let travelerId: string;
    if (event.data) {
      travelerId = event.data.id;
    } else {
      travelerId = event.travelerId || "";
    }
    const { selectedTravelerIds } =
      ctx[ParentState.activityTravelerInformation];

    const newTravelerInfoCtx = {
      ...ctx[ParentState.activityTravelerInformation],
    };

    // Remove traveler from appropriate list if exists and action is not triggered onUpdate
    if (!onUpdate && selectedTravelerIds.includes(travelerId)) {
      const newSelectedTravelerIds = selectedTravelerIds.filter(
        (id) => travelerId !== id
      );
      newTravelerInfoCtx.selectedTravelerIds = newSelectedTravelerIds;
    } else {
      // If traveler not in either list, add to appropriate list
      const { userTravelers } = ctx[ParentState.activityTravelerInformation];
      const newTraveler = userTravelers.find(
        (traveler) => traveler.id === travelerId
      );

      if (!newTraveler) {
        return setContextWithKey(
          ctx,
          ParentState.activityTravelerInformation,
          newTravelerInfoCtx
        );
      }

      // Hacky way to ensure user can only select a single traveler.
      if (singleTravelerWorkflow) {
        newTravelerInfoCtx.selectedTravelerIds = [travelerId];
      } else {
        newTravelerInfoCtx.selectedTravelerIds = uniq([
          ...selectedTravelerIds,
          travelerId,
        ]);
      }
    }

    return setContextWithKey(
      ctx,
      ParentState.activityTravelerInformation,
      newTravelerInfoCtx
    );
  }),

  setUserTravelers: assign(
    (ctx: ActivityContext, event: DoneInvokeEvent<{ value: IPerson[] }>) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityTravelerInformation}.userTravelers`,
        event.data.value
      )
  ),

  handleDeleteTraveler: assign(
    (ctx: ActivityContext, event: DoneInvokeEvent<any>) => {
      const { selectedTravelerIds } =
        ctx[ParentState.activityTravelerInformation];
      const newSelectedTravelerIds = selectedTravelerIds.filter(
        (id) => event.data.deletedPassengerId !== id
      );
      ctx[ParentState.activityTravelerInformation].selectedTravelerIds =
        newSelectedTravelerIds;
      ctx[ParentState.activityTravelerInformation].userTravelers =
        event.data.userPassengers;
      return ctx;
    }
  ),

  setSingleDefaultTraveler: assign((ctx: ActivityContext) => {
    const { userTravelers, selectedTravelerIds } =
      ctx[ParentState.activityTravelerInformation];

    // Always clear currentUserTraveler first to prevent stale state from previous interactions
    ctx[ParentState.activityTravelerInformation].currentUserTraveler = undefined;

    if (userTravelers.length === 1) {
      const singleTraveler = userTravelers[0];

      // If the single traveler has missing required fields, set them as currentUserTraveler
      // so the form opens for editing instead of auto-selecting them
      if (travelerHasMissingRequiredFields(singleTraveler, ctx)) {
        ctx[ParentState.activityTravelerInformation].currentUserTraveler =
          singleTraveler;
      } else if (!selectedTravelerIds.includes(singleTraveler.id)) {
        // Only add to selectedTravelerIds if not already selected
        ctx[ParentState.activityTravelerInformation].selectedTravelerIds.push(
          singleTraveler.id
        );
      }
    }
    return ctx;
  }),

  clearCurrentTraveler: assign((ctx: ActivityContext) => {
    ctx[ParentState.activityTravelerInformation].currentUserTraveler = undefined;
    return ctx;
  }),

  setCurrentTraveler: assign(
    (ctx: ActivityContext, event: setCurrentTravelerEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityTravelerInformation}.currentUserTraveler`,
        event.traveler
      )
  ),

  setTravelersError: assign(
    (ctx: ActivityContext, event: DoneInvokeEvent<PassengerError>) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityTravelerInformation}.error`,
        event.data
      )
  ),

  clearTravelerInformationError: assign((ctx: ActivityContext) => {
    ctx[ParentState.activityTravelerInformation].error = undefined;
    return ctx;
  }),

  setTravelerVisited: assign((ctx: ActivityContext) =>
    setContextWithKey(
      ctx,
      `${ParentState.activityTravelerInformation}.visited`,
      true
    )
  ),

  setValidated: assign((ctx: ActivityContext) =>
    setContextWithKey(
      ctx,
      `${ParentState.activityTravelerInformation}.isValidated`,
      true
    )
  ),

  resetValidated: assign((ctx: ActivityContext) =>
    setContextWithKey(
      ctx,
      `${ParentState.activityTravelerInformation}.isValidated`,
      false
    )
  ),

  selectPrimaryTraveler: assign(
    (ctx: ActivityContext, event: SelectTravelerEvent) => {
      const travelerId = event.data?.id || event.travelerId;

      const newPassengerInfoCtx = {
        ...ctx[ParentState.activityPrimaryTraveler],
      };

      newPassengerInfoCtx.selectedPrimaryTravelerId = travelerId;

      return {
        ...ctx,
        [ParentState.activityPrimaryTraveler]: newPassengerInfoCtx,
      };
    }
  ),

  setPerTravelerBookingQuestions: assign(
    (ctx: ActivityContext, event: AnswerBookingQuestionsEvent) => {
      const { travelerId, data } = event;

      ctx[ParentState.activityTravelerInformation].bookingQuestionResponse = {
        ...ctx[ParentState.activityTravelerInformation].bookingQuestionResponse,
        [travelerId]: data,
      };

      return ctx;
    }
  ),

  setPrimaryTravelerToSingleSelected: assign((ctx: ActivityMachineContext) => {
    if (!isCheckoutImprovementsEnabled(ctx.experiments)) {
      return ctx;
    }

    const { selectedTravelerIds } =
      ctx[ParentState.activityTravelerInformation];

    if (selectedTravelerIds.length !== 1) {
      return ctx;
    }

    return {
      ...ctx,
      [ParentState.activityPrimaryTraveler]: {
        ...ctx[ParentState.activityPrimaryTraveler],
        selectedPrimaryTravelerId: selectedTravelerIds[0],
      },
    };
  }),
};

export const ActionTypes = getObjectKeysAsObject(actions);
